import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import { Popover } from '@mui/material';

import { Page, TableContainer, Button, TableHeader, snackbar } from '@occc-app/design-system';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useGetOfferByIds } from '@occc-app/dataAccessLayer/offer/queries';
import {
  useDeleteKioskAdSetMutation,
  usePublishKioskAdSetMutation,
  useArchiveKioskAdSetMutation,
} from '@occc/graphql';

import { AdSetsListTable } from './ad-sets-list.table';
import { AdSetsListActions } from './components/ad-sets-list-actions';
import { AdSetsFilterForm, AdSetFilterFormValues } from './components/ad-sets-filter-form';

import { useGetKioskAdSetLazy } from '../../hooks/use-get-ad-set.hook';
import { AdSetsDeleteConfirmDialog } from '../../components/ad-sets-delete-confirm-dialog.component';
import { AdSetPublishDialog } from '../../components/ad-set-publish-dialog.component';
import { AdSetArchiveDialog } from '../../components/ad-set-archive-dialog.component';
import { useStatusFilter } from '../../hooks/use-status-filter.hook';
import { useGetAdSets } from '../../hooks/use-get-ad-sets.hook';
import { routeCreator } from '../../touchpoint.router';
import {
  AD_SET_DELETE_ERROR_MESSAGE,
  AD_SET_DELETE_SUCCESS_MESSAGE,
  AD_SET_PUBLISH_SUCCESS_MESSAGE,
  AD_SET_PUBLISH_ERROR_MESSAGE,
  AD_SET_ARCHIVE_SUCCESS_MESSAGE,
  AD_SET_ARCHIVE_ERROR_MESSAGE,
} from '../../touchpoint.constants';
import { validateAdSetForPublish } from '../../utils/ad-set-publish-validation';
import { getOfferDetailsUrl, OfferLinkInfo } from '../../utils/offer-url';
import { AdSetPublishValidationErrors } from '../../components/ad-set-publish-dialog.component';

const PAGE_TITLE = 'Ad Sets';

type DialogType = 'delete' | 'publish' | 'archive' | null;

export const AdSetsListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [status, setStatus] = useStatusFilter();
  const [paginationInfo, onPageChange] = usePaginationParams();
  const [selectedAdSetId, setSelectedAdSetId] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);

  const isUserPermittedToCreateAdSet = useIsUserPermittedTo(Feature.TOUCHPOINT_AD_SET_CREATE);

  const filterPopupState = usePopupState({ variant: 'popover', popupId: 'filters' });

  const adSetFilter = useMemo(() => {
    return {
      searchTerm: search || undefined,
      status: status ? status : undefined,
    };
  }, [search, status]);

  const { loading, error, data, paginationProps, refetch } = useGetAdSets({
    filter: adSetFilter,
    paginationInfo,
    onPageChange,
  });

  const offerIds = useMemo(() => {
    if (!data) return [];

    return data.map((adSet) => adSet.offerId).filter((id): id is string => Boolean(id));
  }, [data]);

  const { data: offersData } = useGetOfferByIds(offerIds, undefined, {
    skip: offerIds.length === 0,
  });

  const offersMap = useMemo(() => {
    if (!offersData) return new Map<string, OfferLinkInfo>();

    return new Map(
      offersData.map((offer) => [
        offer.id,
        {
          id: offer.id,
          name: offer.name,
          url: getOfferDetailsUrl(offer.id, offer.engagementType, offer.campaign?.uuid),
        },
      ]),
    );
  }, [offersData]);

  const [deleteKioskAdSet, { loading: loadingDelete }] = useDeleteKioskAdSetMutation();
  const [publishKioskAdSet, { loading: loadingPublish }] = usePublishKioskAdSetMutation();
  const [archiveKioskAdSet, { loading: loadingArchive }] = useArchiveKioskAdSetMutation();
  const { executeQuery: fetchFullAdSet, response: fullAdSetResponse } = useGetKioskAdSetLazy();
  const { loading: loadingFullAdSet } = fullAdSetResponse;

  const [validationErrorsForPublish, setValidationErrorsForPublish] =
    useState<AdSetPublishValidationErrors | null>(null);

  const selectedAdSet = useMemo(() => {
    if (!selectedAdSetId || !data) return null;
    return data.find((adSet) => adSet.id === selectedAdSetId) || null;
  }, [selectedAdSetId, data]);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setSelectedAdSetId(null);
    setValidationErrorsForPublish(null);
  }, []);

  const handleDelete = useCallback((adSetId: string) => {
    setSelectedAdSetId(adSetId);
    setActiveDialog('delete');
  }, []);

  const handlePublish = useCallback(
    (adSetId: string) => {
      setSelectedAdSetId(adSetId);
      setActiveDialog('publish');
      setValidationErrorsForPublish(null);
      fetchFullAdSet({
        variables: { id: adSetId },
        onCompleted: (data) => {
          if (data.kioskAdSet) {
            setValidationErrorsForPublish(validateAdSetForPublish(data.kioskAdSet));
          }
        },
        onError: () => {
          closeDialog();
        },
      });
    },
    [fetchFullAdSet, closeDialog],
  );

  const handleArchive = useCallback((adSetId: string) => {
    setSelectedAdSetId(adSetId);
    setActiveDialog('archive');
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedAdSetId) return;

    await deleteKioskAdSet({
      variables: { id: selectedAdSetId },
      onCompleted: () => {
        snackbar.success(AD_SET_DELETE_SUCCESS_MESSAGE);
        closeDialog();
      },
      onError: () => {
        snackbar.error(AD_SET_DELETE_ERROR_MESSAGE);
        closeDialog();
      },
      refetchQueries: ['kioskAdSets'],
    });
  }, [deleteKioskAdSet, selectedAdSetId, closeDialog]);

  const handleConfirmPublish = useCallback(async () => {
    if (!selectedAdSetId) return;

    await publishKioskAdSet({
      variables: { id: selectedAdSetId },
      onCompleted: () => {
        snackbar.success(AD_SET_PUBLISH_SUCCESS_MESSAGE);
        closeDialog();
        refetch();
      },
      onError: () => {
        snackbar.error(AD_SET_PUBLISH_ERROR_MESSAGE);
        closeDialog();
      },
    });
  }, [publishKioskAdSet, selectedAdSetId, closeDialog, refetch]);

  const handleConfirmArchive = useCallback(async () => {
    if (!selectedAdSetId) return;

    await archiveKioskAdSet({
      variables: { id: selectedAdSetId },
      onCompleted: () => {
        snackbar.success(AD_SET_ARCHIVE_SUCCESS_MESSAGE);
        closeDialog();
        refetch();
      },
      onError: () => {
        snackbar.error(AD_SET_ARCHIVE_ERROR_MESSAGE);
        closeDialog();
      },
    });
  }, [archiveKioskAdSet, selectedAdSetId, closeDialog, refetch]);

  const actions = isUserPermittedToCreateAdSet && (
    <Button
      color='secondary'
      variant='contained'
      component={Link}
      to={routeCreator.adSetsCreate()}
      role='button'
    >
      ADD NEW AD SET
    </Button>
  );

  const filterPopoverProps = bindPopover(filterPopupState);

  const handleSearchSubmit = useCallback(
    (values: { search: string }) => {
      setSearch(values.search);
    },
    [setSearch],
  );

  const navigateToAdSetDetails = useCallback(
    (adSetId: string) => {
      navigate(routeCreator.adSetsDetails(adSetId));
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (adSetId: string) => {
      navigate(routeCreator.adSetsUpdate(adSetId));
    },
    [navigate],
  );

  const handleFilterFormSubmit = useCallback(
    (values: AdSetFilterFormValues) => {
      setStatus(values.status);
      filterPopoverProps.onClose();
    },
    [setStatus, filterPopoverProps],
  );

  const handleFilterFormReset = useCallback(() => {
    setStatus('');
    filterPopoverProps.onClose();
  }, [setStatus, filterPopoverProps]);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      actions={actions}
      pageHeaderProps={{
        title: PAGE_TITLE,
        subtitle:
          'Create and manage ad sets for your in-store campaigns. Set scheduling and define targeting to control when and where ads are shown.',
      }}
    >
      <TableContainer>
        <TableHeader
          title={PAGE_TITLE}
          actions={
            <AdSetsListActions
              search={search || ''}
              status={status || ''}
              filterPopupState={filterPopupState}
              onSearchSubmit={handleSearchSubmit}
            />
          }
        />
        <AdSetsListTable
          adSets={data}
          offersMap={offersMap}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          onItemClick={navigateToAdSetDetails}
          onView={navigateToAdSetDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPublish={handlePublish}
          onArchive={handleArchive}
        />
        <Popover
          {...filterPopoverProps}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          keepMounted
          sx={{
            '.MuiPopover-paper': {
              maxWidth: 460,
              minWidth: 300,
            },
          }}
        >
          <AdSetsFilterForm
            status={status || ''}
            onSubmit={handleFilterFormSubmit}
            onReset={handleFilterFormReset}
            onClose={filterPopoverProps.onClose}
          />
        </Popover>
      </TableContainer>

      {activeDialog === 'delete' && selectedAdSet && (
        <AdSetsDeleteConfirmDialog
          adSetName={selectedAdSet.name}
          onClose={closeDialog}
          onConfirm={handleConfirmDelete}
          loading={loadingDelete}
        />
      )}

      {activeDialog === 'publish' && selectedAdSet && (
        <AdSetPublishDialog
          onClose={closeDialog}
          onConfirm={handleConfirmPublish}
          loading={loadingPublish || loadingFullAdSet}
          adSetName={selectedAdSet.name}
          validationErrors={loadingFullAdSet ? null : validationErrorsForPublish}
        />
      )}

      {activeDialog === 'archive' && selectedAdSet && (
        <AdSetArchiveDialog
          onClose={closeDialog}
          onConfirm={handleConfirmArchive}
          loading={loadingArchive}
          adSetName={selectedAdSet.name}
        />
      )}
    </Page>
  );
};
